/**
 * useWorkshop — React Query hooks for the workshop detail page (BON-117).
 *
 * `useWorkshop` loads the full workshop record (with audit + lifecycle)
 * by id; `useUpdateWorkshop` PATCHes a partial change set and writes the fresh
 * workshop back into the cache so the page re-renders from the server's view
 * (the source of truth for derived fields). Publish and discard
 * are one-shot lifecycle mutations that also refresh the cache / lists.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  cancelWorkshop,
  discardWorkshop,
  fetchWorkshopById,
  publishWorkshop,
  replaceWorkshopStaff,
  setBookingCheckIn,
  updateWorkshop,
  WorkshopValidationError,
  type CancelWorkshopBody,
  type CancelWorkshopResult,
  type CrewSlotInput,
  type SetCheckInResult,
  type WorkshopDetail,
  type WorkshopPatch,
} from '../../../api/workshops';

const STALE_30S = 30_000;

export const workshopKey = (id: string) => ['workshop', id] as const;

/** `pollMs` (BON-239): refetch on an interval — the check-in view sets it so
 *  several crew checking in the same workshop see each other's check-ins
 *  near-live. Foreground only; optimistic check-in writes reconcile against
 *  each refetch (server wins). Omit for the normal one-shot read. */
export function useWorkshop(id: string, opts?: { pollMs?: number }) {
  return useQuery({
    queryKey: workshopKey(id),
    queryFn: () => fetchWorkshopById(id),
    enabled: id.length > 0,
    staleTime: STALE_30S,
    refetchInterval: opts?.pollMs ?? false,
    refetchIntervalInBackground: false,
  });
}

/** Invalidate the dashboards surfaces that list workshops (calendars, etc.). */
export function invalidateLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['dashboards.admin'] });
  qc.invalidateQueries({ queryKey: ['dashboards.regional'] });
  qc.invalidateQueries({ queryKey: ['dashboards.teacher'] });
}

/** PATCH a workshop; refreshes this workshop's cache and the lists. */
export function useUpdateWorkshop(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: WorkshopPatch) => updateWorkshop(id, patch),
    onSuccess: (result) => {
      qc.setQueryData(workshopKey(id), result.workshop);
      invalidateLists(qc);
    },
  });
}

/** PUT /:id/staff — full crew replacement (BON-200). Refreshes this workshop's
 *  cache (so the rail re-renders from the server's crew) and the lists. */
export function useReplaceWorkshopStaff(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (staff: CrewSlotInput[]) => replaceWorkshopStaff(id, staff),
    onSuccess: (result) => {
      qc.setQueryData(workshopKey(id), result.workshop);
      invalidateLists(qc);
    },
  });
}

/** The shape applied to the cache after a check-in write (mirrors the server
 *  `SetCheckInResult`, plus a synthesized `checkedInAt` for the optimistic pass). */
interface CheckInPatch {
  bookingId: string;
  checkedInCount: number;
  attendeeCount: number;
  checkedInAt: string | null;
}

/**
 * Apply a partial check-in result to a cached workshop, recomputing the roster's
 * `checkedInSeats` as Σ `checkedInCount` over ACTIVE (CONFIRMED) bookings
 * (BON-204 R2). Pure — exported for unit testing and reused by both the
 * optimistic update and the server-confirmed update. Returns a no-op-shaped copy
 * when the booking isn't in the roster.
 */
export function applyCheckInToWorkshop(
  workshop: WorkshopDetail,
  result: CheckInPatch,
): WorkshopDetail {
  const { roster } = workshop;
  const bookings = roster.bookings.map((b) =>
    b.bookingId === result.bookingId
      ? { ...b, checkedInCount: result.checkedInCount, checkedInAt: result.checkedInAt }
      : b,
  );
  const checkedInSeats = bookings
    .filter((b) => b.status === 'CONFIRMED')
    .reduce((sum, b) => sum + b.checkedInCount, 0);
  return { ...workshop, roster: { ...roster, bookings, checkedInSeats } };
}

/**
 * PUT …/check-in — partial per-booking check-in by COUNT (BON-204 R2).
 * Optimistically writes the booking's count in the `useWorkshop` cache (so the
 * modal re-sorts and the seat headline updates instantly — "Saved live"), then
 * reconciles with the server's response. On error the prior snapshot is rolled
 * back. `checkedInCount === 0` is an undo (clears the booking).
 */
export function useSetCheckIn(workshopId: string) {
  const qc = useQueryClient();
  return useMutation<
    SetCheckInResult,
    Error,
    { bookingId: string; checkedInCount: number; attendeeCount: number },
    { previous?: WorkshopDetail }
  >({
    mutationFn: ({ bookingId, checkedInCount }) =>
      setBookingCheckIn(workshopId, bookingId, checkedInCount),
    onMutate: async ({ bookingId, checkedInCount, attendeeCount }) => {
      await qc.cancelQueries({ queryKey: workshopKey(workshopId) });
      const previous = qc.getQueryData<WorkshopDetail>(workshopKey(workshopId));
      if (previous) {
        qc.setQueryData(
          workshopKey(workshopId),
          applyCheckInToWorkshop(previous, {
            bookingId,
            checkedInCount,
            attendeeCount,
            checkedInAt: checkedInCount > 0 ? new Date().toISOString() : null,
          }),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(workshopKey(workshopId), ctx.previous);
    },
    onSuccess: (result) => {
      const current = qc.getQueryData<WorkshopDetail>(workshopKey(workshopId));
      if (current) {
        qc.setQueryData(workshopKey(workshopId), applyCheckInToWorkshop(current, result));
      }
    },
  });
}

/**
 * POST /:id/publish — draft → active. Exposes a `run()` that swallows the
 * rejection into `errorMessage` so the page can render it inline. A readiness
 * 409 (BON-215) carries a `fieldErrors` map (thrown as {@link
 * WorkshopValidationError}); that map is exposed separately so the confirm modal
 * can surface it per-field as a backstop for a race, while `errorMessage` still
 * covers non-field failures (already-active, network). `reset()` clears both so
 * the page can wipe stale errors when the modal reopens.
 */
export function usePublishWorkshop(id: string) {
  const qc = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | null>(null);
  const mutation = useMutation({
    mutationFn: () => publishWorkshop(id),
    onSuccess: (workshop) => {
      qc.setQueryData(workshopKey(id), workshop);
      invalidateLists(qc);
    },
  });
  const reset = () => {
    setErrorMessage(null);
    setFieldErrors(null);
  };
  /** Returns true when the publish landed, false when it failed (so the caller
   *  — e.g. the confirm modal — can close on success and stay open on error). */
  const run = async (): Promise<boolean> => {
    reset();
    try {
      await mutation.mutateAsync();
      return true;
    } catch (e) {
      if (e instanceof WorkshopValidationError) {
        setFieldErrors(e.fieldErrors);
        setErrorMessage(e.message);
      } else {
        setErrorMessage(e instanceof Error ? e.message : 'Could not publish.');
      }
      return false;
    }
  };
  return { run, reset, isPending: mutation.isPending, errorMessage, fieldErrors };
}

/** POST /:id/cancel. On success the source's cache is invalidated so the page
 *  refetches the full `cancelled` record. The mutation REJECTS on a refusal so
 *  the confirm dialog can stay open and show the reason. */
export function useCancelWorkshop(id: string) {
  const qc = useQueryClient();
  return useMutation<CancelWorkshopResult, Error, CancelWorkshopBody>({
    mutationFn: (body: CancelWorkshopBody) => cancelWorkshop(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workshopKey(id) });
      invalidateLists(qc);
    },
  });
}

/** DELETE /:id discard flow + its transient state. On success the page swaps
 *  to the DRAFT DISCARDED empty state. */
export function useDiscardState() {
  const [discarded, setDiscarded] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (id: string) => {
    setError(null);
    setDiscarding(true);
    try {
      await discardWorkshop(id);
      setDiscarded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not discard.');
    } finally {
      setDiscarding(false);
    }
  };
  return { discarded, discarding, error, run };
}
